import { useContext, useEffect, useState } from "react"
import tw from "twin.macro"
import { LanguageContext } from "../../../contexts/Language"
import { useTealiumContext } from "../../../contexts/Tealium"
import useTealiumEvent from "../../../hooks/Tealium/useTealiumEvent"
import Coupons from "../../molecules/Coupons/Coupons"
import { Coupon } from "../../molecules/Coupons/Coupons.d"
import {
  firstLettersCapital,
  parseDisclaimerBlocks,
  removeExpiredCoupons,
} from "../../../helpers"
import { toggleDisclaimersModal } from "../../../contexts/Disclaimers/actions"
import { ContactContext } from "../../../contexts/Contact"
import {
  tabAnimation,
  focusAnimation,
  activeAnimation,
} from "../../../styles/animations"

const CarCareCouponSection: React.FC<{
  coupons: Coupon[]
  heading: string
  selectedParts: string[]
  category: string
  allCoupons: Coupon[]
}> = ({ coupons, heading, selectedParts, category, allCoupons }) => {
  const [_state, dispatch] = useContext(ContactContext)
  const allValidCoupons = removeExpiredCoupons(
    allCoupons.map(coupon => ({
      ...coupon,
      pillLabel: "Coupon",
    })),
  )
  const allValidCatCoupons = removeExpiredCoupons(
    coupons.map(coupon => ({
      ...coupon,
      pillLabel: category == "all" ? "Coupon" : firstLettersCapital(category),
      vehicleCategory: category,
    })),
  )
  const [filteredCoupons, setFilteredCoupons] = useState<(Coupon | null)[]>(
    allValidCatCoupons.filter(coupon => coupon !== null),
  )
  const { _ } = useContext(LanguageContext)

  const couponCategories =
    category == "Parts" && selectedParts.length == 0
      ? ["Parts", "All"]
      : category == "Parts" && selectedParts.length > 0
        ? [...selectedParts, "Parts", "All"]
        : null

  const [selectedFilter, setSelectedFilter] = useState(
    couponCategories && couponCategories[0].toLowerCase(),
  )

  useEffect(() => {
    if (couponCategories) {
      filterCoupons(couponCategories[0]?.toLowerCase(), false)
      setSelectedFilter(couponCategories && couponCategories[0].toLowerCase())
    }
  }, [selectedParts])

  //Tealium
  const { trackTealEvent } = useTealiumEvent()
  const { tealPageData } = useTealiumContext()

  const filterCoupons = (selectedCategory: string, onClickEvent: boolean) => {
    setSelectedFilter(selectedCategory.toLowerCase())
    if (selectedCategory.toLowerCase() === "all" && allValidCoupons) {
      setFilteredCoupons(
        allValidCoupons.map(coupon => ({
          ...coupon,
          pillLabel: "Coupon",
          vehicleCategory: category,
        })),
      )
      onClickEvent &&
        trackTealEvent({
          tealium_event: "refinement",
          refinement_value: `${tealPageData?.page_type}|${tealPageData?.page_name}|Coupon Type|${selectedCategory}`,
        })
    } else if (selectedCategory.toLowerCase() === category.toLowerCase()) {
      setFilteredCoupons(
        allValidCatCoupons.map(coupon => ({
          ...coupon,
          pillLabel: category == "all" ? "Coupon" : category,
          vehicleCategory: category,
        })),
      )
      onClickEvent &&
        trackTealEvent({
          tealium_event: "refinement",
          refinement_value: `${tealPageData?.page_type}|${tealPageData?.page_name}|Coupon Type|${selectedCategory}`,
        })
    } else {
      setFilteredCoupons(
        allValidCoupons
          ?.filter(
            (coupon: any) =>
              coupon.type.toLowerCase() === selectedCategory.toLowerCase(),
          )
          .map(coupon => ({
            ...coupon,
            pillLabel: firstLettersCapital(selectedCategory),
            vehicleCategory: category,
          })),
      )
      onClickEvent &&
        trackTealEvent({
          tealium_event: "refinement",
          refinement_value: `${tealPageData?.page_type}|${tealPageData?.page_name}|Coupon Type|${selectedCategory}`,
        })
    }
  }

  return (
    <section
      aria-label="Coupon Section"
      css={[tw`border-t border-gray-400 max-w-[1440px] mx-auto w-full`]}
    >
      <h2
        css={[
          tw`col-span-1 text-center text-2xl font-light  tracking-widest mt-10  md:(text-3xl) lg:(col-span-3 mt-20)`,
        ]}
      >
        {parseDisclaimerBlocks(heading, selection =>
          dispatch(toggleDisclaimersModal(selection)),
        )}
      </h2>
      {couponCategories && (
        <ul
          css={[
            tw`flex gap-6 overflow-x-scroll w-full px-4 my-2 scrollbar-hide py-1.5`,
            tw`md:(justify-center)`,
          ]}
        >
          {couponCategories?.map((cat, index) => (
            <button
              onClick={() => filterCoupons(cat, true)}
              aria-selected={selectedFilter === cat.toLowerCase()}
              role="tab"
              css={[
                tabAnimation,
                focusAnimation,
                tw`cursor-pointer whitespace-nowrap inline-block relative py-2 mx-2`,
                selectedFilter === cat.toLowerCase() && activeAnimation,
              ]}
              analytics-id={`coupon filter:coupon:${index + 1}`}
            >
              {_(cat)} {_("Coupons")}
            </button>
          ))}
        </ul>
      )}
      <Coupons
        coupons={filteredCoupons}
        showTitle={false}
        css={tw`relative z-50 bg-none`}
      />
    </section>
  )
}

export default CarCareCouponSection
